import * as React from "react";
import Dropzone from "react-dropzone";
import {Option} from "ts-option";
import ButtonTertiary from "../../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import {api} from "../../../../api/api";
import ImageUtils from "../../../../utils/ImageUtils";

interface Props {
	/**
	 * Saved logo, shown until another one is dropped or it is removed.
	 */
	logoId: Option<string>;
	onDropFile: (file: File) => void;
	onRemove: () => void;
}

interface State {
	/**
	 * What the logo box shows: the saved logo as downloaded, or the dropped file. Empty for no logo.
	 */
	src: string | null;
	/**
	 * Indicates if the saved logo has been replaced or removed on the page.
	 */
	changed: boolean;
}

/**
 * The partner's logo as it looks, next to a drop zone to replace it.
 */
export default class PartnerBrandLogoField extends React.PureComponent<Props, State> {
	imageExtensions = ".png,.jpg,.jpeg,.jpe,.gif";
	mounted = false;
	/**
	 * Object URL of the dropped file, released once it is no longer shown.
	 */
	objectUrl: string | null = null;

	constructor(props: Props) {
		super(props);
		this.state = {src: null, changed: false};
	}

	componentDidMount() {
		this.mounted = true;
		// The download needs the session, so the saved logo cannot be an <img> pointing at the API.
		this.props.logoId.forEach(id => api.instance.attachmentApi.downloadFile(id)
			.then(buffer => ImageUtils.getImageAsSrcString(buffer, (src: string) => {
				if (this.mounted && !this.state.changed) {
					this.setState({src});
				}
			}))
			.catch(() => {}));
	}

	componentWillUnmount() {
		this.mounted = false;
		this.releaseObjectUrl();
	}

	render() {
		return (
			<div className={"c-pb-logo"}>
				<div className={"c-pb-logo_preview"}>
					{this.state.src ?
						<img src={this.state.src} alt={"Logo"}/> :
						<span className={"c-pb-logo_empty"}>No logo</span>}
				</div>
				<Dropzone onDrop={this.onDrop}
						  accept={this.imageExtensions}
						  className={"c-pb-logo_dropzone"}
						  activeClassName={"is-active"}
						  multiple={false}>
					<span className={"t-link"}>Drop a logo here or click to upload</span>
				</Dropzone>
				{this.state.src && <ButtonTertiary label={"Remove"} onClick={this.onRemove}/>}
			</div>
		);
	}

	onDrop = (acceptedFiles: File[]) => {
		const [file] = acceptedFiles;
		if (!file) {
			return;
		}
		this.releaseObjectUrl();
		this.objectUrl = URL.createObjectURL(file);
		this.setState({src: this.objectUrl, changed: true});
		this.props.onDropFile(file);
	}

	onRemove = () => {
		this.releaseObjectUrl();
		this.setState({src: null, changed: true});
		this.props.onRemove();
	}

	releaseObjectUrl() {
		if (this.objectUrl) {
			URL.revokeObjectURL(this.objectUrl);
			this.objectUrl = null;
		}
	}
}
